import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import type { Employee } from '../types';
import type { User } from '@supabase/supabase-js';

interface AuthContextType {
  user: Employee | null;
  authUser: User | null;
  loading: boolean;
  login: (employee: Employee) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Employee | null>(null);
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const isInitializing = useRef(true); // Flag pour éviter la race condition

  // Refs pour éviter les stale closures dans onAuthStateChange
  const authUserRef = useRef<User | null>(null);
  const userRef = useRef<Employee | null>(null);

  // Récupérer les données employé à partir de l'auth_user_id
  const fetchEmployee = async (authUserId: string): Promise<Employee | null> => {
    console.log('[AuthContext] fetchEmployee appelé avec authUserId:', authUserId);

    try {
      // Sélectionner tous les champs SAUF pin_hash (sécurité)
      const { data, error } = await supabase
        .from('employees')
        .select('id, created_at, full_name, short_name, role, is_interim, is_active, manager_id, phone, interim_agency, comments, company, custom_weekly_hours, auth_user_id')
        .eq('auth_user_id', authUserId)
        .single();

      if (error) {
        console.error('[AuthContext] Erreur récupération employé:', error);
        return null;
      }

      console.log('[AuthContext] Employé trouvé:', data?.full_name);
      return data as Employee;
    } catch (err) {
      console.error('[AuthContext] Exception fetchEmployee:', err);
      return null;
    }
  };

  // Synchroniser les refs avec les states pour éviter les stale closures
  useEffect(() => {
    authUserRef.current = authUser;
  }, [authUser]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Initialisation : vérifier la session existante
  useEffect(() => {
    let isMounted = true; // Pour éviter les mises à jour après unmount
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const initializeAuth = async () => {
      console.log('[AuthContext] Initialisation...');

      // Timeout de sécurité (10 secondes max) pour éviter le blocage infini
      timeoutId = setTimeout(() => {
        if (isMounted && isInitializing.current) {
          console.error('[AuthContext] ⚠️ TIMEOUT - initialisation trop longue (10s)');
          isInitializing.current = false;
          setLoading(false);
        }
      }, 10000);

      try {
        // Récupérer la session actuelle
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (!isMounted) return; // Composant démonté pendant l'attente
        if (timeoutId) clearTimeout(timeoutId); // Annuler le timeout si succès

        console.log('[AuthContext] Session récupérée:', session ? 'OUI' : 'NON', sessionError || '');

        if (session?.user) {
          console.log('[AuthContext] User trouvé dans session:', session.user.id, session.user.email);
          setAuthUser(session.user);
          const employee = await fetchEmployee(session.user.id);
          if (!isMounted) return;
          if (employee) {
            setUser(employee);
          } else {
            console.warn('[AuthContext] Employé non trouvé pour auth_user_id:', session.user.id);
            // Déconnecter si l'employé n'existe pas (compte orphelin)
            await supabase.auth.signOut();
          }
        } else {
          console.log('[AuthContext] Pas de session active');
        }
      } catch (error) {
        if (timeoutId) clearTimeout(timeoutId);
        console.error('[AuthContext] Erreur initialisation auth:', error);
      } finally {
        if (isMounted) {
          console.log('[AuthContext] Fin initialisation, loading=false');
          isInitializing.current = false; // Marquer la fin de l'initialisation
          setLoading(false);
        }
      }
    };

    initializeAuth();

    // Écouter les changements d'authentification
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('[AuthContext] onAuthStateChange:', event, session?.user?.id || 'no user');

        // IMPORTANT: Ignorer les événements pendant l'initialisation pour éviter la race condition
        if (isInitializing.current) {
          console.log('[AuthContext] Événement ignoré pendant initialisation');
          return;
        }

        if (event === 'SIGNED_IN' && session?.user) {
          // Vérifier si on n'est pas déjà connecté avec le même utilisateur
          // (évite le re-fetch inutile au retour d'onglet)
          // IMPORTANT: Utiliser les refs pour avoir les valeurs actuelles (pas stale closure)
          if (authUserRef.current?.id === session.user.id && userRef.current) {
            console.log('[AuthContext] SIGNED_IN ignoré - déjà connecté avec cet utilisateur');
            return;
          }

          setLoading(true); // Montrer le loader pendant le fetch
          setAuthUser(session.user);
          const employee = await fetchEmployee(session.user.id);
          if (employee) {
            setUser(employee);
          } else {
            console.warn('[AuthContext] Employé non trouvé après SIGNED_IN');
            await supabase.auth.signOut();
          }
          setLoading(false); // TOUJOURS remettre loading à false
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          // Token JWT rafraîchi (ex: retour d'onglet après inactivité)
          // Juste mettre à jour l'authUser, pas besoin de refetch l'employé
          console.log('[AuthContext] Token rafraîchi');
          setAuthUser(session.user);
          // Ne PAS toucher à loading ni user - ils sont déjà corrects
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setAuthUser(null);
          setLoading(false);
        }
      }
    );

    // Cleanup
    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, []);

  // Login manuel (utilisé par Login.tsx après signInWithPassword)
  const login = (employee: Employee) => {
    setUser(employee);
  };

  // Logout : déconnexion Supabase Auth
  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setAuthUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, authUser, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
